import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import Controls from "./components/Controls";
import HubspotPageTypeSelector from "./components/HubspotPageTypeSelector";
import HubspotPagesDashboard from "./components/HubspotPagesDashboard";
import SelectedLandingPage from "./components/SelectedLandingPage";
import classes from "./LandingPageSelectionSlide.module.scss";
import { BaseStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { CampaignPageTypes } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster } from "../../../helpers/toasters";
import { getHubspotPages, setCampaignLandingPage } from "../../../redux/reducers/campaignToCreate";

type HubspotPageTypeSelectorValues = {
  landingPage: boolean;
  webPage: boolean;
  blogPage: boolean;
};

const LandingPageSelectionSlide: FC<BaseStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}) => {
  const foundHubspotPages = useSelector((state: ReduxApplicationState) => state.campaignToCreate.foundHubspotPages);
  const isHubspotPagesPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const selectedLandingPageFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.landingPage,
  );

  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);

  const [selectedLandingPage, setSelectedLandingPage] = useState(() => {
    return selectedLandingPageFromStore || "";
  });
  const [hubspotPageTypeSelectorValues, setHubspotPageTypeSelectorValues] = useState<HubspotPageTypeSelectorValues>({
    landingPage: false,
    webPage: false,
    blogPage: false,
  });

  const dispatch = useDispatch();

  const handleHubspotPageTypeSelectorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHubspotPageTypeSelectorValues({
      ...hubspotPageTypeSelectorValues,
      [event.target.name]: event.target.checked,
    });
  };

  const handleGetPages = async () => {
    const pageTypes: CampaignPageTypes[] = [];
    if (hubspotPageTypeSelectorValues.blogPage) {
      pageTypes.push(CampaignPageTypes.blogPosts);
    }

    if (hubspotPageTypeSelectorValues.landingPage) {
      pageTypes.push(CampaignPageTypes.landingPage);
    }

    if (hubspotPageTypeSelectorValues.webPage) {
      pageTypes.push(CampaignPageTypes.websitePage);
    }
    dispatch(getHubspotPages(pageTypes));
  };

  const handleSelectLandingPage = (landingPage: string) => {
    setSelectedLandingPage(landingPage);
  };

  useEffect(() => {
    if (isAttemptingToGoNext) {
      if (selectedLandingPage) {
        dispatch(setCampaignLandingPage(selectedLandingPage));
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select a destination page to continue.", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [dispatch, handleNext, isAttemptingToGoNext, selectedLandingPage, setIsAttemptingToGoNext]);

  return (
    <CampaignCreationSlideContainer heading="Campaign Landing Page Selection">
      <div className={classes.container}>
        <Controls selectedLandingPage={selectedLandingPage} handleSelectLandingPage={handleSelectLandingPage} />
        <SelectedLandingPage selectedLandingPage={selectedLandingPage} />

        {selectedAdvertiser?.hubspot_authed && (
          <HubspotPageTypeSelector
            values={hubspotPageTypeSelectorValues}
            handleChange={handleHubspotPageTypeSelectorChange}
            handleGetPages={handleGetPages}
            isLoading={isHubspotPagesPending}
          />
        )}

        {!selectedAdvertiser?.hubspot_authed && (
          <p className={classes.header}>
            Please use the Enter custom URL option to continue. To select a landing page, web page, or blog post from
            HubSpot, the selected advertiser needs to have their HubSpot account connected.
          </p>
        )}

        {foundHubspotPages.length !== 0 ? (
          <HubspotPagesDashboard
            hubspotPages={foundHubspotPages}
            handleSelectLandingPage={handleSelectLandingPage}
            selectedLandingPage={selectedLandingPage}
          />
        ) : null}
      </div>
    </CampaignCreationSlideContainer>
  );
};

export default LandingPageSelectionSlide;
